import React, { useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Polyline } from '@react-google-maps/api';
import config from '../config';
import { useTheme } from '../contexts/ThemeContext';
import {
  extractActivityId,
  calculateBounds,
  calculateRouteBounds,
  hslToHex,
  ANIMATION_CONFIG
} from '../utils/mapUtils';

const containerStyle = {
  width: '100%',
  height: '100vh'
};

// Dark mode map style
const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a1a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#3f3f46' }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#71717a' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#27272a' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#71717a' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#1f2f1f' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#27272a' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#18181b' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#3f3f46' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#27272a' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#27272a' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#0c1929' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#3b82f6' }],
  },
];

// Light mode map style
const lightMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#c9c9c9' }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#bdbdbd' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#eeeeee' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#757575' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#c8e6c9' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e0e0e0' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#dadada' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#c9c9c9' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#e5e5e5' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c9d7e8' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9e9e9e' }],
  },
];

const RaceMap = ({ races, routes, selectedRace, onRaceSelect }) => {
  const { theme } = useTheme();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMapsApiKey
  });

  const [map, setMap] = React.useState(null);
  const mapStyle = theme === 'dark' ? darkMapStyle : lightMapStyle;

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);

    // Auto-fit bounds to show all routes
    const bounds = calculateBounds(routes);
    if (bounds) {
      mapInstance.fitBounds(bounds);
    }
  }, [routes]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Update map style when theme changes
  React.useEffect(() => {
    if (map) {
      map.setOptions({ styles: mapStyle });
    }
  }, [map, mapStyle]);

  // Zoom to selected race when it changes
  React.useEffect(() => {
    if (!map || !selectedRace || !routes) return;

    const activityId = extractActivityId(selectedRace.strava);
    const coordinates = routes[activityId];
    const bounds = calculateRouteBounds(coordinates);

    let timeoutId = null;

    if (bounds) {
      map.fitBounds(bounds);

      // Add a bit of padding/zoom out for better view
      timeoutId = setTimeout(() => {
        const currentZoom = map.getZoom();
        if (currentZoom > ANIMATION_CONFIG.MAX_ZOOM_LEVEL) {
          map.setZoom(ANIMATION_CONFIG.MAX_ZOOM_LEVEL);
        }
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedRace, map, routes]);

  // Calculate animated offset for rainbow colors - only runs when a route is selected
  const [glowHue, setGlowHue] = React.useState(0);

  React.useEffect(() => {
    if (!selectedRace) return;

    const animate = () => {
      setGlowHue(prev => (prev + 1) % 360);
    };

    const intervalId = setInterval(animate, ANIMATION_CONFIG.ANIMATION_INTERVAL);
    return () => clearInterval(intervalId);
  }, [selectedRace]);

  // Function to reset zoom to show all routes
  const resetZoom = useCallback(() => {
    const bounds = calculateBounds(routes);
    if (map && bounds) {
      map.fitBounds(bounds);
    }
  }, [map, routes]);

  // Get route color based on race type
  const getRouteColor = (race) => {
    if (!race) return '#3388ff';
    
    // You can customize color logic here
    if (race.type === 'Ultra') {
      return '#FF5733'; // Red for ultras
    } else {
      return '#3357FF'; // Blue for marathons
    }
  };

  // Get route opacity and weight
  const getRouteStyle = (activityId) => {
    const isSelected = selectedRace && 
                       extractActivityId(selectedRace.strava) === activityId;
    
    if (isSelected) {
      // Selected route gets animated glow effect (handled separately)
      return {
        strokeOpacity: 0.3,
        strokeWeight: 2,
        zIndex: 999,
        strokeColor: '#9CA3AF' // Gray for unselected appearance
      };
    }
    
    return {
      strokeOpacity: 0.4,
      strokeWeight: 2,
      zIndex: 1,
      strokeColor: '#9CA3AF'
    };
  };

  // Handle route click
  const handleRouteClick = (activityId) => {
    const race = races.find(r => extractActivityId(r.strava) === activityId);
    if (race && onRaceSelect) {
      onRaceSelect(race);
    }
  };

  if (!isLoaded) {
    return <div className="loading">Loading Maps...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={config.map.defaultCenter}
      zoom={config.map.defaultZoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        styles: mapStyle
      }}
    >
      {/* Reset zoom button */}
      {selectedRace && (
        <button
          onClick={resetZoom}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            padding: '10px 16px',
            background: theme === 'dark' ? '#27272a' : '#ffffff',
            border: '2px solid #22c55e',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: '#22c55e',
            boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#22c55e';
            e.target.style.color = theme === 'dark' ? '#09090b' : '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = theme === 'dark' ? '#27272a' : '#ffffff';
            e.target.style.color = '#22c55e';
          }}
        >
          View All Routes
        </button>
      )}
      
      {/* Render all race routes */}
      {races && routes && races.map(race => {
        const activityId = extractActivityId(race.strava);
        const coordinates = routes[activityId];
        
        if (!coordinates || coordinates.length === 0) return null;
        
        const isSelected = selectedRace && 
                          extractActivityId(selectedRace.strava) === activityId;
        const style = getRouteStyle(activityId);
        
        return (
          <React.Fragment key={activityId}>
            {/* Base route line */}
            <Polyline
              path={coordinates}
              options={{
                strokeColor: isSelected ? '#667eea' : getRouteColor(race),
                strokeOpacity: isSelected ? 0.3 : style.strokeOpacity,
                strokeWeight: style.strokeWeight,
                zIndex: style.zIndex,
                clickable: true
              }}
              onClick={() => handleRouteClick(activityId)}
            />
            
            {/* Animated rainbow gradient effect for selected route */}
            {isSelected && (
              <>
                {(() => {
                  const { NUM_SEGMENTS, STROKE_WEIGHT, STROKE_OPACITY } = ANIMATION_CONFIG;
                  const segmentLength = coordinates.length / NUM_SEGMENTS;
                  const rainbowSegments = [];

                  for (let i = 0; i < NUM_SEGMENTS; i++) {
                    const positionFactor = (i / NUM_SEGMENTS) * 360;
                    const hue = (glowHue - positionFactor + 360) % 360;
                    const color = hslToHex(hue, 100, 50);

                    const startIdx = Math.floor(i * segmentLength);
                    const endIdx = Math.floor((i + 1) * segmentLength);
                    const segmentCoords = coordinates.slice(startIdx, endIdx + 1);

                    if (segmentCoords.length > 1) {
                      rainbowSegments.push(
                        <Polyline
                          key={`rainbow-segment-${i}`}
                          path={segmentCoords}
                          options={{
                            strokeColor: color,
                            strokeOpacity: STROKE_OPACITY,
                            strokeWeight: STROKE_WEIGHT,
                            zIndex: 1001,
                            clickable: true
                          }}
                          onClick={() => handleRouteClick(activityId)}
                        />
                      );
                    }
                  }

                  return rainbowSegments;
                })()}
              </>
            )}
          </React.Fragment>
        );
      })}
    </GoogleMap>
  );
};

export default RaceMap;
